// The board of a puzzle, in React: the puzzle's controller, the board it is
// played on, and the strip under it. The rest of what a puzzle shows — the
// status, the line to step through, the buttons — is the screen's, drawn from
// the same snapshot (see `usePuzzle`).

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { BoardView } from "./surface.ts";
import { Puzzle, type Host, type PuzzleOptions, type Snapshot } from "./puzzle.ts";

declare global {
  interface Window {
    /** The puzzle on the page, for the browser tests to read its state. */
    __puzzle?: Puzzle;
  }
}

/**
 * A puzzle for as long as the component showing it lives, and the snapshot
 * of it to render. A new `key` on the component is a new puzzle; the options
 * are read once. The host is read afresh on every call, so the screen's
 * handlers can close over its latest state.
 */
export function usePuzzle(options: PuzzleOptions, host: Host): [Puzzle, Snapshot] {
  const hostRef = useRef(host);
  hostRef.current = host;
  const [puzzle] = useState(
    () =>
      new Puzzle(options, {
        onMove: (m) => hostRef.current.onMove?.(m),
        onWrong: (w) => hostRef.current.onWrong?.(w),
        onSolved: () => hostRef.current.onSolved?.(),
        onFault: (f) => hostRef.current.onFault?.(f),
        askEngine: host.askEngine ? (fen) => hostRef.current.askEngine!(fen) : undefined,
        askScore: host.askScore ? (fen) => hostRef.current.askScore!(fen) : undefined,
      })
  );
  useEffect(() => () => puzzle.destroy(), [puzzle]);
  const snapshot = useSyncExternalStore(puzzle.subscribe, puzzle.getSnapshot);
  return [puzzle, snapshot];
}

export function PuzzleBoard({ puzzle, snapshot, boardStyle, pieces }: { puzzle: Puzzle; snapshot: Snapshot; boardStyle?: string; pieces?: string }) {
  const el = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!el.current) return;
    const view = new BoardView(el.current, {
      fen: puzzle.shownFen(),
      orientation: puzzle.solver === "b" ? "black" : "white",
      style: boardStyle,
      pieces,
      onPick: (square) => puzzle.canPickUp(square),
      onDrop: (from, to) => puzzle.dropped(from, to),
    });
    puzzle.attach(view);
    window.__puzzle = puzzle;
    return () => {
      if (window.__puzzle === puzzle) delete window.__puzzle;
      view.destroy();
    };
  }, [puzzle, boardStyle, pieces]);
  return (
    <div className="board-zone">
      <div className="board" ref={el} />
      <ScoreStrip score={snapshot.score} />
    </div>
  );
}

export function ScoreStrip({ score }: { score: Snapshot["score"] }) {
  return (
    <div className="score" title="What the position on the board is worth, from White's side">
      <div className="score-bar">
        <div className="score-white" style={{ width: `${score.whiteShare}%` }} />
      </div>
      <span className="score-num">{score.text}</span>
    </div>
  );
}
